import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  IconButton,
  VStack,
  HStack,
  Button,
  useColorMode,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  FaRegHeart,
  FaHeart,
  FaCity,
  FaPlaneDeparture,
  FaRegCalendarAlt,
} from "react-icons/fa";

import axiosInstance from "../../helpers/axiosInstance";
import { formatDate } from "../../helpers/utils";

import { useCurrency } from "../CurrencyExchange/CurrencyContext"; // Import Currency Context
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";

const FlightCard = ({
  data,
  category = "transportation",
  itinerary = false,
  conversationId,
  route,
  onFlightsChange,
  fromItineray,
  fromChatItineray,
}) => {
  // console.log("FlightCard",data);
  const [current, setCurrent] = useState(0);
  const { colorMode } = useColorMode();
  const [likedActivities, setLikedActivities] = useState({});
  const { convertCurrency, currency } = useCurrency(); // Get the conversion function and selected currency

  // Adjust number of visible cards based on screen size
  const numVisibleCards = useBreakpointValue({ base: 1, md: 2, lg: 2 });
  // Safeguard against undefined or non-array data
  const numTotalCards = Array.isArray(data) ? data.length : 0;

  const containerRef = useRef(null);
  const touchStartXRef = useRef(null);

  const [showCallout, setShowCallout] = useState(true);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setShowCallout(false), 9000);
    return () => clearTimeout(timer);
  }, []); // run once on mount

  const next = () => {
    setCurrent((prev) => (prev + numVisibleCards) % numTotalCards);
  };

  const prev = () => {
    setCurrent(
      (prev) => (prev - numVisibleCards + numTotalCards) % numTotalCards
    );
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    if (diff > 50) next();
    else if (diff < -50) prev();
  };

  const hasPrev = current > 0;
  const hasNext = current + numVisibleCards < numTotalCards;

  const isActivityLiked = (idx, item) =>
    likedActivities[idx] ?? item.added_to_bucket_list ?? false;

  const sendLikedActivity = async (activity, added) => {
    const payload = {
      // flight_info: [activity], // Send only the current liked/unliked activity
      conversation_data: activity, // Send only the current liked/unliked activity
      // category: category,
      conversation_type: "transportation_conversation",
      added_to_bucket_list: added,
    };
    try {
      await axiosInstance.post(
        `/chat/conversation/update-conversation-heart-icon/${conversationId}`,
        payload
      );
      // Call the second endpoint only when flight is liked
      if (added) {
        const bucketPayload = {
          data: activity,
          category: category,
          conversation_type: "transportation_conversation",
        };
        await axiosInstance.post(
          "/bucket-list/save-third-party-service",
          bucketPayload
        );
      }
      // console.log(`Activity ${isLiked ? "liked" : "unliked"}:`, activity);
    } catch (error) {
      console.error("Error saving liked activity:", error);
    }
  };

  // const toggleLike = (index, activity) => {
  //   const updatedLikes = { ...likedActivities };
  //   const isLiked = !updatedLikes[index];

  //   if (isLiked) {
  //     updatedLikes[index] = activity; // Add activity if liked
  //   } else {
  //     delete updatedLikes[index]; // Remove activity if unliked
  //   }

  //   setLikedActivities(updatedLikes);

  //   // Send only the current activity to backend
  //   sendLikedActivity(activity, isLiked);
  // };

  const toggleLike = (index, activity) => {
    const currentlyLiked = isActivityLiked(index, activity);
    const newLiked = !currentlyLiked; // white → red  OR  red → white

    setLikedActivities((prev) => ({ ...prev, [index]: newLiked }));
    sendLikedActivity(activity, newLiked); // <-- pass TRUE or FALSE
  };

  // Delete a specific flight
  const handleDeleteFlight = async (flightIndex) => {
    if (!conversationId || !route) return;
    setIsDeleting((prev) => ({ ...prev, [flightIndex]: true }));
    try {
      await axiosInstance.delete(
        `/chat/conversation/delete-flight-info/${conversationId}`,
        {
          data: {
            route: route,
            flight_index: flightIndex,
          },
        }
      );
      if (onFlightsChange) onFlightsChange(flightIndex, "single");
    } catch (error) {
      console.error("Error deleting flight:", error);
    } finally {
      setIsDeleting((prev) => ({ ...prev, [flightIndex]: false }));
    }
  };
  return (
    <Flex
      position="relative"
      alignItems="center"
      justifyContent="center"
      padding={0}
      mt={3}
      width={{ base: "100%", md: "80%", lg: "100%" }} // Responsive width
    >
      {hasPrev && (
        <IconButton
          icon={<ArrowBackIcon />}
          isRound={true}
          size="md"
          onClick={prev}
          position="absolute"
          left={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
      <HStack
        ref={containerRef}
        overflow="hidden"
        width="full"
        h="auto"
        spacing={4}
        justifyContent={{ base: "center", md: "space-between" }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {data.slice(current, current + numVisibleCards).map((item, index) => {
          // Convert the flight price
          const convertedPrice = convertCurrency(item.price, "USD"); // Assuming the base price is in USD
          const actualIndex = current + index;
          {
            /* const isLiked = likedActivities[actualIndex] !== undefined; */
          }
          const liked = isActivityLiked(actualIndex, item);

          return (
            <Box
              key={index}
              bg="blackAlpha.50"
              padding={2}
              // m={2}
              borderRadius="xl"
              boxShadow="md"
              width={{ base: "80%", md: "50%", lg: "50%" }}
              // onClick={() =>
              //   onActivityClick &&
              //   onActivityClick(
              //     data.attractionLatitude,
              //     data.attractionLongitude
              //   )
              // }
              display="flex"
              flexDirection="column"
              alignItems="flex-start" // Align items to the left
            >
              <VStack
                p={2}
                borderRadius="xl"
                alignItems="flex-start"
                h="100%"
                width="100%"
                spacing={3}
                bg="white"
                position="relative"
              >
                {/* Live price and Powered by */}
                <Box width="100%">
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "sm", md: "md" }}
                    mb={0}
                  >
                    <Translate text="Price:" /> {currency} {convertedPrice}
                  </Text>
                  {/* Delete and Heart icons */}
                  <Box
                    position="absolute"
                    top="3px"
                    right="3px"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="0px"
                  >
                    {fromChatItineray && (
                      <Tooltip label="Delete Flight">
                        <IconButton
                          aria-label="Delete flight"
                          icon={<DeleteIcon />}
                          size="md"
                          color="black"
                          isLoading={isDeleting[actualIndex]}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteFlight(actualIndex);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                        />
                      </Tooltip>
                    )}
                    {!fromItineray && !fromChatItineray && (
                      <Tooltip
                        label={
                          liked
                            ? "Remove from bucket list"
                            : "Add to bucket list"
                        }
                      >
                        <IconButton
                          aria-label={
                            liked
                              ? "Remove from bucket list"
                              : "Add to bucket list"
                          }
                          icon={liked ? <FaHeart /> : <FaRegHeart />}
                          size="md"
                          color={liked ? "red.500" : "black"}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(actualIndex, item);
                          }}
                          borderRadius="full"
                          bg="none"
                          _hover={{ bg: "none" }}
                          marginLeft="-12px"
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color="gray.500"
                    mb={0}
                  >
                    <Translate text="Airline: " />
                    {item.airline}
                  </Text>
                </Box>

                {/* Flight Information */}
                <HStack
                  justify="space-between"
                  width="full"
                  spacing={0}
                  bg="blackAlpha.50"
                  padding={2}
                  borderRadius="xl"
                >
                  <VStack align="center" spacing={0}>
                    <Text
                      fontWeight="bold"
                      mb={0}
                      fontSize={{ base: "lg", md: "xs" }}
                    >
                      <Box as="span" mr={2} display="inline-block">
                        <FaCity />
                      </Box>
                      {item.origin}
                    </Text>
                    <Text
                      fontWeight="bold"
                      mb={0}
                      fontSize={{ base: "lg", md: "xs" }}
                    >
                      <Box as="span" mr={2} display="inline-block">
                        <FaPlaneDeparture />
                      </Box>
                      {item.origin_airport}
                    </Text>
                    <Text
                      fontSize={{ base: "xs", md: "xs" }}
                      mb={0}
                      color="gray.500"
                      mt={2}
                    >
                      {/* <Box as="span" mr={1} display="inline-block"><FaRegCalendarAlt /></Box> */}
                      {new Date(item.departure_at).toLocaleDateString()}
                    </Text>
                  </VStack>
                  <VStack align="center" spacing={0}>
                    <Text fontSize={{ base: "sm", md: "xs" }} mb={0}>
                      {Math.floor(item.duration_to / 60)}h{" "}
                      {item.duration_to % 60}m
                    </Text>
                    {item.return_transfers === 1 ? (
                      <VStack spacing={0}>
                        <Text fontSize={{ base: "sm", md: "xs" }} mb={0}>
                          ➔
                        </Text>
                        <Text
                          fontSize={{ base: "sm", md: "xs" }}
                          mb={0}
                          style={{
                            transform: "scaleX(-1)",
                            display: "inline-block",
                            marginTop: "-6px",
                          }}
                        >
                          ➔
                        </Text>
                      </VStack>
                    ) : (
                      <Text fontSize={{ base: "sm", md: "xs" }} mb={0}>
                        ➔
                      </Text>
                    )}
                    <Text
                      fontSize={{ base: "xs", md: "xs" }}
                      color="gray.500"
                      mb={0}
                      mt={2}
                    >
                      {item.transfers > 0
                        ? `${item.transfers} stop`
                        : "Non-stop"}
                    </Text>
                  </VStack>
                  <VStack align="center" spacing={0}>
                    <Text
                      fontWeight="bold"
                      fontSize={{ base: "lg", md: "xs" }}
                      mb={0}
                    >
                      {item.destination}
                    </Text>
                    <Text
                      fontWeight="bold"
                      fontSize={{ base: "lg", md: "xs" }}
                      mb={0}
                    >
                      {item.destination_airport}
                    </Text>
                    <Text
                      fontSize={{ base: "xs", md: "xs" }}
                      color="gray.500"
                      mb={0}
                      mt={2}
                    >
                      {new Date(item.return_at).toLocaleDateString()}
                    </Text>
                  </VStack>
                </HStack>
                {/* Open Button */}
                <ExternalLinkButton
                  as="a"
                  href={item.link}
                  size={{ base: "md", md: "sm", lg: "sm" }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  bg="#286328"
                  _hover="none"
                  _active="none"
                  textColor="white"
                  width="full"
                >
                  <Translate text="Open" />
                </ExternalLinkButton>
              </VStack>
            </Box>
          );
        })}
      </HStack>
      {hasNext && (
        <IconButton
          icon={<ArrowForwardIcon />}
          isRound={true}
          size="md"
          onClick={next}
          position="absolute"
          right={{ base: "1px", md: "10px" }}
          backgroundColor="white"
          boxShadow="base"
          zIndex="overlay"
        />
      )}
    </Flex>
  );
};

export default FlightCard;
